import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { canPreviewInBrowser } from "@/lib/documentPreview";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Upload, FileText, Trash2, Eye, Download, X, File, FileImage, FileSpreadsheet, Sparkles, Files, Pencil } from "lucide-react";
import { DocumentPreview } from "@/components/ui/DocumentPreview";
import { DocumentCategoryConfirmDialog, PendingDocument } from "@/components/DocumentCategoryConfirmDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { de } from "date-fns/locale";

interface EmployeeDocumentsTableProps {
  employeeId: string;
  readOnly?: boolean;
}

interface EmployeeDocument {
  id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  category: string;
  description: string | null;
  created_at: string;
}

// Fallback-Kategorien falls keine aus der Datenbank kommen
const FALLBACK_CATEGORIES = [
  "Allgemein",
  "Qualifikationen", 
  "Führerschein",
  "Sonstiges"
] as const;

// Category display configuration
const CATEGORY_CONFIG: Record<string, { color: string; icon: string; order: number }> = {
  "Arbeitsverträge": { color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200", icon: "📄", order: 1 },
  "Vertragsänderungen": { color: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200", icon: "📝", order: 2 },
  "Zertifikate": { color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200", icon: "🏆", order: 3 },
  "Jubiläumsurkunden": { color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200", icon: "🎉", order: 4 },
  "Abmahnungen": { color: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200", icon: "⚠️", order: 5 },
  "Benefits": { color: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200", icon: "🎁", order: 6 },
  "BAV": { color: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200", icon: "💰", order: 7 },
  "Sonstiges": { color: "bg-muted text-muted-foreground", icon: "📁", order: 8 },
};

// Confidence threshold for auto-save (70%)
const AUTO_SAVE_CONFIDENCE_THRESHOLD = 0.7;

export function EmployeeDocumentsTable({ employeeId, readOnly = false }: EmployeeDocumentsTableProps) {
  const { user, userRole } = useAuth();
  const { getReadableCategories, getUploadableCategories, isAdmin, isSelfContext } = usePermissions();
  const [documents, setDocuments] = useState<EmployeeDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("Sonstiges");
  const [description, setDescription] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<EmployeeDocument | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropzoneRef = useRef<HTMLDivElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [useAiAnalysis, setUseAiAnalysis] = useState(true);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [editingDocId, setEditingDocId] = useState<string | null>(null);
  const [editingCategory, setEditingCategory] = useState<string>("Sonstiges");
  const [editingFileName, setEditingFileName] = useState<string>("");
  const [editingDescription, setEditingDescription] = useState<string>("");
  const [savingCategory, setSavingCategory] = useState(false);
  
  // NEW: State for category confirmation dialog
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [pendingAnalysis, setPendingAnalysis] = useState<PendingDocument[]>([]);
  const [tempFilesForConfirmation, setTempFilesForConfirmation] = useState<File[]>([]);
  const [confirmingSave, setConfirmingSave] = useState(false);

  // Dynamische Kategorien basierend auf Berechtigungen
  const uploadableCategories = useMemo(() => {
    const cats = getUploadableCategories(employeeId);
    return cats.length > 0 ? cats.map(c => c.category_name) : [...FALLBACK_CATEGORIES];
  }, [getUploadableCategories, employeeId]);

  // Lesebare Kategorien für die Anzeige
  const readableCategories = useMemo(() => {
    return getReadableCategories(employeeId);
  }, [getReadableCategories, employeeId]);

  // Setze Standard-Kategorie auf erste verfügbare
  useEffect(() => {
    if (uploadableCategories.length > 0 && !uploadableCategories.includes(selectedCategory)) {
      setSelectedCategory(uploadableCategories[0]);
    }
  }, [uploadableCategories, selectedCategory]);

  // HR staff can upload documents
  const canManage = userRole === "admin" || userRole === "hr_manager" || userRole === "hr_clerk";
  // Only admins and hr_managers can change document categories
  const canEditCategory = userRole === "admin" || userRole === "hr_manager";
  // Only admins can delete documents
  const canDelete = userRole === "admin";
  const canUpload = !readOnly && (canManage || userRole === "employee");

  useEffect(() => {
    loadDocuments();
  }, [employeeId]);

  const loadDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from("employee_documents")
        .select("*")
        .eq("employee_id", employeeId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error("Error loading documents:", error);
      toast.error("Fehler beim Laden der Dokumente");
    } finally {
      setLoading(false);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading && !analyzing) {
      setIsDragOver(true);
    }
  }, [uploading, analyzing]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    if (uploading || analyzing || !user) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      processFiles(droppedFiles);
    }
  }, [uploading, analyzing, user]);

  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !user) return;

    processFiles(Array.from(files));
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const processFiles = async (files: File[]) => {
    // Validate all files
    const validFiles: File[] = [];
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} ist zu groß. Maximal 10MB erlaubt.`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    if (useAiAnalysis) {
      await handleAiUpload(validFiles);
    } else {
      await handleStandardUpload(validFiles);
    }
  };

  const handleStandardUpload = async (files: File[]) => {
    if (!user) return;
    setUploading(true);

    try {
      let successCount = 0;
      
      for (const file of files) {
        const filePath = `${employeeId}/${Date.now()}-${file.name}`;

        // Upload to storage
        const { error: uploadError } = await supabase.storage
          .from("employee-documents")
          .upload(filePath, file);

        if (uploadError) {
          console.error(`Error uploading ${file.name}:`, uploadError);
          toast.error(`Fehler beim Hochladen von ${file.name}`);
          continue;
        }

        // Create document record
        const { error: insertError } = await supabase
          .from("employee_documents")
          .insert({
            employee_id: employeeId,
            file_name: file.name,
            file_path: filePath,
            file_size: file.size,
            mime_type: file.type,
            category: selectedCategory,
            description: description || null,
            uploaded_by: user.id
          });

        if (insertError) {
          console.error(`Error creating record for ${file.name}:`, insertError);
          toast.error(`Fehler beim Speichern von ${file.name}`);
          continue;
        }

        successCount++;
      }

      if (successCount > 0) {
        toast.success(`${successCount} Dokument(e) erfolgreich hochgeladen`);
        setDescription("");
        await loadDocuments();
      }
    } catch (error: any) {
      console.error("Error uploading documents:", error);
      toast.error(error.message || "Fehler beim Hochladen der Dokumente");
    } finally {
      setUploading(false);
    }
  };

  const handleAiUpload = async (files: File[]) => {
    if (!user) return;
    setAnalyzing(true);

    try {
      const formData = new FormData();
      files.forEach(file => formData.append("files", file));
      formData.append("employeeId", employeeId);
      formData.append("userId", user.id);
      // NEW: First analyze without saving
      formData.append("autoSave", "false");

      const { data, error } = await supabase.functions.invoke("parse-employee-documents", {
        body: formData,
      });

      if (error) throw error;

      if (!data.success) {
        throw new Error(data.error || "KI-Analyse fehlgeschlagen");
      }

      const categorizedFiles = data.categorizedFiles as Array<{
        file_name: string;
        category: string;
        description: string;
        confidence: number;
      }>;

      // Separate documents into auto-save (high confidence) and needs-confirmation (low confidence)
      const autoSaveFiles: File[] = [];
      const autoSaveCategories: Array<{ fileName: string; category: string; description: string | null }> = [];
      const needsConfirmation: PendingDocument[] = [];

      for (const catFile of categorizedFiles) {
        const file = files.find(f => f.name === catFile.file_name);
        if (!file) continue;

        const isHighConfidence = catFile.confidence >= AUTO_SAVE_CONFIDENCE_THRESHOLD;
        const isSonstiges = catFile.category === "Sonstiges";

        if (isHighConfidence && !isSonstiges) {
          // Auto-save high confidence documents (not "Sonstiges")
          autoSaveFiles.push(file);
          autoSaveCategories.push({
            fileName: file.name,
            category: catFile.category,
            description: catFile.description || null,
          });
        } else {
          // Needs manual confirmation
          needsConfirmation.push({
            fileName: file.name,
            category: catFile.category,
            description: catFile.description || "",
            confidence: catFile.confidence,
            file: file,
          });
        }
      }

      // Auto-save high-confidence documents
      if (autoSaveFiles.length > 0) {
        await saveDocumentsWithCategories(autoSaveFiles, autoSaveCategories);
        toast.success(`${autoSaveFiles.length} Dokument(e) automatisch kategorisiert und gespeichert`);
      }

      // Show dialog for low-confidence documents
      if (needsConfirmation.length > 0) {
        setPendingAnalysis(needsConfirmation);
        setTempFilesForConfirmation(needsConfirmation.map(d => d.file));
        setShowCategoryDialog(true);
      } else if (autoSaveFiles.length > 0) {
        await loadDocuments();
      }
    } catch (error: any) {
      console.error("Error with AI upload:", error);
      toast.error(error.message || "Fehler bei der KI-Analyse");
    } finally {
      setAnalyzing(false);
    }
  };

  // NEW: Save documents with confirmed categories
  const saveDocumentsWithCategories = async (
    files: File[],
    categories: Array<{ fileName: string; category: string; description: string | null }>
  ) => {
    if (!user) return;

    const formData = new FormData();
    files.forEach(file => formData.append("files", file));
    formData.append("employeeId", employeeId);
    formData.append("userId", user.id);
    formData.append("autoSave", "true");
    formData.append("categories", JSON.stringify(categories));

    const { data, error } = await supabase.functions.invoke("parse-employee-documents", {
      body: formData,
    });

    if (error) throw error;

    if (!data.success) {
      throw new Error(data.error || "Speichern fehlgeschlagen");
    }

    return data;
  };

  // NEW: Handle confirmation from dialog
  const handleConfirmCategories = async (confirmedDocuments: PendingDocument[]) => {
    if (!user) return;
    setConfirmingSave(true);

    try {
      const files = confirmedDocuments.map(d => d.file);
      const categories = confirmedDocuments.map(d => ({
        fileName: d.fileName,
        category: d.category,
        description: d.description || null,
      }));

      await saveDocumentsWithCategories(files, categories);

      toast.success(`${confirmedDocuments.length} Dokument(e) erfolgreich gespeichert`);
      setShowCategoryDialog(false);
      setPendingAnalysis([]);
      setTempFilesForConfirmation([]);
      await loadDocuments();
    } catch (error: any) {
      console.error("Error saving confirmed documents:", error);
      toast.error(error.message || "Fehler beim Speichern der Dokumente");
    } finally {
      setConfirmingSave(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    const doc = documents.find(d => d.id === deleteId);
    if (!doc) return;

    setDeleting(true);
    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from("employee-documents")
        .remove([doc.file_path]);

      if (storageError) throw storageError;

      // Delete record
      const { error: dbError } = await supabase
        .from("employee_documents")
        .delete()
        .eq("id", deleteId);

      if (dbError) throw dbError;

      toast.success("Dokument erfolgreich gelöscht");
      setDeleteId(null);
      await loadDocuments();
    } catch (error: any) {
      console.error("Error deleting document:", error);
      toast.error(error.message || "Fehler beim Löschen des Dokuments");
    } finally {
      setDeleting(false);
    }
  };

  const handlePreview = (doc: EmployeeDocument) => {
    setSelectedDocument(doc);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setSelectedDocument(null);
  };

  const handleEditCategory = (doc: EmployeeDocument) => {
    setEditingDocId(doc.id);
    setEditingCategory(doc.category || "Sonstiges");
    setEditingFileName(doc.file_name);
    setEditingDescription(doc.description || "");
  };

  const handleSaveCategory = async () => {
    if (!editingDocId) return;
    
    setSavingCategory(true);
    try {
      const updateData: Record<string, string> = { category: editingCategory };
      if (editingFileName.trim()) {
        updateData.file_name = editingFileName.trim();
      }
      updateData.description = editingDescription || null as any;

      const { error } = await supabase
        .from("employee_documents")
        .update(updateData)
        .eq("id", editingDocId);

      if (error) throw error;

      toast.success("Dokument erfolgreich aktualisiert");
      setEditingDocId(null);
      loadDocuments();
    } catch (error: any) {
      console.error("Error updating document:", error);
      toast.error("Fehler beim Aktualisieren des Dokuments");
    } finally {
      setSavingCategory(false);
    }
  };

  const handleDownload = async (doc: EmployeeDocument) => {
    try {
      const { data, error } = await supabase.storage
        .from("employee-documents")
        .download(doc.file_path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error("Error downloading document:", error);
      toast.error("Fehler beim Herunterladen des Dokuments");
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getCategoryBadgeColor = (category: string) => {
    return CATEGORY_CONFIG[category]?.color || "bg-muted text-muted-foreground";
  };

  // Filter documents based on readable categories (use readableCategories from above)
  const filteredDocuments = useMemo(() => {
    // Admins and users viewing their own documents see everything
    if (isAdmin || isSelfContext(employeeId)) {
      return documents;
    }

    // Filter by readable category names
    const readableCategoryNames = readableCategories.map(c => c.category_name);
    return documents.filter(doc => {
      const category = doc.category || "Sonstiges";
      return readableCategoryNames.includes(category);
    });
  }, [documents, readableCategories, isAdmin, isSelfContext, employeeId]);

  // Group filtered documents by category
  const groupedDocuments = filteredDocuments.reduce((acc, doc) => {
    const category = doc.category || "Sonstiges";
    if (!acc[category]) acc[category] = [];
    acc[category].push(doc);
    return acc;
  }, {} as Record<string, EmployeeDocument[]>);

  // Sort categories by configured order
  const sortedCategories = Object.keys(groupedDocuments).sort((a, b) => {
    const orderA = CATEGORY_CONFIG[a]?.order || 99;
    const orderB = CATEGORY_CONFIG[b]?.order || 99;
    return orderA - orderB;
  });

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith("image/")) return FileImage;
    if (mimeType.includes("spreadsheet") || mimeType.includes("excel")) return FileSpreadsheet;
    if (mimeType.includes("pdf")) return FileText;
    return File;
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      {canUpload && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Dokumente hochladen
            </CardTitle>
            <CardDescription>
              Ziehen Sie Dokumente hierher oder wählen Sie Dateien aus (max. 10MB pro Datei)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* AI Analysis Toggle */}
            <div className="flex items-center gap-4 p-3 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="ai-analysis"
                  checked={useAiAnalysis}
                  onCheckedChange={(checked) => setUseAiAnalysis(checked as boolean)}
                />
                <Label htmlFor="ai-analysis" className="flex items-center gap-2 cursor-pointer">
                  <Sparkles className="h-4 w-4 text-primary" />
                  KI-Kategorisierung aktivieren
                </Label>
              </div>
              {useAiAnalysis && (
                <p className="text-xs text-muted-foreground">
                  Dokumente werden automatisch erkannt und kategorisiert
                </p>
              )}
            </div>

            {/* Manual category selection only if AI is disabled */}
            {!useAiAnalysis && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Kategorie</Label>
                  <Select value={selectedCategory} onValueChange={(v) => setSelectedCategory(v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {uploadableCategories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Beschreibung (optional)</Label>
                  <Input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="z.B. Arbeitsvertrag 2024"
                  />
                </div>
              </div>
            )}

            {/* Drag & Drop Zone */}
            <div
              ref={dropzoneRef}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => !uploading && !analyzing && fileInputRef.current?.click()}
              className={`
                relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all
                ${isDragOver 
                  ? "border-primary bg-primary/10 scale-[1.02]" 
                  : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/30"
                }
                ${(uploading || analyzing) && "opacity-50 cursor-not-allowed"}
              `}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileInputChange}
                disabled={uploading || analyzing}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                className="hidden"
              />

              {uploading || analyzing ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="h-10 w-10 animate-spin text-primary" />
                  <div>
                    <p className="font-medium">
                      {analyzing ? "KI analysiert Dokumente..." : "Dokumente werden hochgeladen..."}
                    </p>
                    <p className="text-sm text-muted-foreground">Bitte warten</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-center">
                    {useAiAnalysis ? (
                      <div className="p-3 rounded-full bg-primary/10">
                        <Sparkles className="h-10 w-10 text-primary" />
                      </div>
                    ) : (
                      <div className="p-3 rounded-full bg-muted">
                        <Files className="h-10 w-10 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-foreground">
                      {useAiAnalysis 
                        ? "Dokumente für KI-Analyse hier ablegen" 
                        : "Dokumente hier ablegen"
                      }
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      oder klicken zum Auswählen (mehrere Dateien möglich)
                    </p>
                  </div>
                  <div className="flex flex-wrap justify-center gap-2 text-xs text-muted-foreground">
                    <Badge variant="outline">PDF</Badge>
                    <Badge variant="outline">DOC/DOCX</Badge>
                    <Badge variant="outline">XLS/XLSX</Badge>
                    <Badge variant="outline">Bilder</Badge>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Documents Overview by Category */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Dokumentenübersicht ({documents.length})
          </CardTitle>
          <CardDescription>
            Alle Dokumente nach Kategorie gruppiert
          </CardDescription>
        </CardHeader>
        <CardContent>
          {documents.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              Keine Dokumente vorhanden
            </p>
          ) : (
            <div className="space-y-6">
              {sortedCategories.map((category) => {
                const categoryDocs = groupedDocuments[category];
                const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG["Sonstiges"];
                
                return (
                  <div key={category} className="space-y-3">
                    <div className="flex items-center gap-2 pb-2 border-b">
                      <span className="text-lg">{config.icon}</span>
                      <h3 className="font-semibold text-foreground">{category}</h3>
                      <Badge variant="secondary" className="ml-auto">
                        {categoryDocs.length} {categoryDocs.length === 1 ? "Dokument" : "Dokumente"}
                      </Badge>
                    </div>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Datei</TableHead>
                            <TableHead className="hidden sm:table-cell">Größe</TableHead>
                            <TableHead className="hidden md:table-cell">Datum</TableHead>
                            <TableHead className="text-right">Aktionen</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {categoryDocs.map((doc) => {
                            const FileIcon = getFileIcon(doc.mime_type);
                            return (
                              <TableRow key={doc.id}>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <FileIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                                    <div className="min-w-0">
                                      <p className="font-medium truncate max-w-[250px]">{doc.file_name}</p>
                                      {doc.description && (
                                        <p className="text-xs text-muted-foreground truncate max-w-[250px]">
                                          {doc.description}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="hidden sm:table-cell text-muted-foreground">
                                  {formatFileSize(doc.file_size)}
                                </TableCell>
                                <TableCell className="hidden md:table-cell text-muted-foreground">
                                  {format(new Date(doc.created_at), "dd.MM.yyyy", { locale: de })}
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center justify-end gap-1">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handlePreview(doc)}
                                      title="Vorschau"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleDownload(doc)}
                                      title="Herunterladen"
                                    >
                                      <Download className="h-4 w-4" />
                                    </Button>
                                    {canEditCategory && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleEditCategory(doc)}
                                        title="Kategorie ändern"
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                    )}
                                    {canDelete && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => setDeleteId(doc.id)}
                                        title="Löschen"
                                      >
                                        <Trash2 className="h-4 w-4 text-destructive" />
                                      </Button>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unified Document Preview Component */}
      <DocumentPreview
        open={previewOpen}
        onOpenChange={(open) => {
          if (!open) closePreview();
          else setPreviewOpen(open);
        }}
        document={selectedDocument}
        bucketName="employee-documents"
        documentType="employee"
        allDocuments={documents.filter(d => canPreviewInBrowser(d.mime_type, d.file_name))}
        onDocumentChange={(doc) => setSelectedDocument(doc as EmployeeDocument)}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dokument löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Sind Sie sicher, dass Sie dieses Dokument löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting}>
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Document Dialog */}
      <Dialog open={!!editingDocId} onOpenChange={(open) => !open && setEditingDocId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Dokument bearbeiten
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Dateiname</Label>
              <Input
                value={editingFileName}
                onChange={(e) => setEditingFileName(e.target.value)}
                placeholder="Dateiname"
              />
            </div>
            <div className="space-y-2">
              <Label>Beschreibung (optional)</Label>
              <Input
                value={editingDescription}
                onChange={(e) => setEditingDescription(e.target.value)}
                placeholder="z.B. Arbeitsvertrag 2024"
              />
            </div>
            <div className="space-y-2">
              <Label>Kategorie</Label>
              <Select 
                value={editingCategory} 
                onValueChange={(v) => setEditingCategory(v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {uploadableCategories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={() => setEditingDocId(null)}
              disabled={savingCategory}
            >
              Abbrechen
            </Button>
            <Button 
              onClick={handleSaveCategory}
              disabled={savingCategory || !editingFileName.trim()}
            >
              {savingCategory ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Speichern
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* NEW: Category Confirmation Dialog */}
      <DocumentCategoryConfirmDialog
        open={showCategoryDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowCategoryDialog(false);
            setPendingAnalysis([]);
            setTempFilesForConfirmation([]);
          }
        }}
        pendingDocuments={pendingAnalysis}
        categories={uploadableCategories}
        onConfirm={handleConfirmCategories}
        isLoading={confirmingSave}
      />
    </div>
  );
}